// src/pages/CargaMasiva/CargaMasiva.js
//
// Dashboard del módulo Carga Masiva.
// Fase 1: listado de lotes + crear lote + métricas por estado.
// Fases siguientes agregan import Excel, auditoría, generación PDF,
// envío PJ y pasaje a Ejecuciones.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Button, IconButton, Tooltip, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
    TablePagination, Chip, CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    FactCheck as FactCheckIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    UploadFile as UploadFileIcon,
    Description as DescriptionIcon,
    Assignment as AssignmentIcon,
    Article as ArticleIcon,
    Gavel as GavelIcon,
    PictureAsPdf as PdfIcon,
    Send as SendIcon,
    CheckCircle as CheckIcon,
    Edit as EditIcon,
    SearchOff as SearchOffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { usePermisos } from '../../utils/usePermisos';
import {
    cargaMasivaListarLotesJWT,
    cargaMasivaCrearLoteJWT,
    cargaMasivaEliminarLoteJWT,
    cargaMasivaMetricasJWT,
    getCombosJWT,
} from '../../api/endpoints';
import ExpedienteSelector from '../../components/ReadjudicacionMasiva/Auditoria/ExpedienteSelector';

const COLOR = '#6a1b9a'; // púrpura Carga Masiva
const BORDER_RADIUS = 4;

const ESTADOS = {
    0: { label: 'Borrador', color: 'default' },
    1: { label: 'Importado', color: 'info' },
    2: { label: 'En auditoría', color: 'warning' },
    3: { label: 'Auditado', color: 'primary' },
    4: { label: 'Abogados asignados', color: 'primary' },
    5: { label: 'PDFs generados', color: 'secondary' },
    // Lo pone la sincronización de firmas cuando están todas las cartas poder: no es el envío.
    6: { label: 'Cartas poder firmadas', color: 'success' },
    7: { label: 'Pasado a SIGIF', color: 'success' },
    8: { label: 'Finalizado', color: 'success' },
    [-1]: { label: 'Anulado', color: 'error' },
};

export default function CargaMasiva() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { tienePermiso } = usePermisos();

    const puedeCrear = tienePermiso('CARGA MASIVA', 'INSERT');
    const puedeEliminar = tienePermiso('CARGA MASIVA', 'DELETE');

    const [lotes, setLotes] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [metricas, setMetricas] = useState(null);
    const [reparticiones, setReparticiones] = useState([]);

    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroModulo, setFiltroModulo] = useState('');
    const [filtroBusqueda, setFiltroBusqueda] = useState('');

    const [dialogCrearOpen, setDialogCrearOpen] = useState(false);
    const [crearForm, setCrearForm] = useState({ idModulo: '', idReparticion: '', observaciones: '', expAdm: null, cantidadEsperada: '' });
    const [crearLoading, setCrearLoading] = useState(false);

    const [dialogEliminarOpen, setDialogEliminarOpen] = useState(false);
    const [loteAEliminar, setLoteAEliminar] = useState(null);

    const cargarLotes = useCallback(async () => {
        setLoading(true);
        try {
            const filtros = {
                estado: filtroEstado,
                idModulo: filtroModulo,
                busqueda: filtroBusqueda,
                page: page + 1,
                pageSize,
            };
            const resp = await cargaMasivaListarLotesJWT(filtros);
            if (resp?.ok) {
                setLotes(resp.lotes || []);
                setTotal(resp.total || 0);
            } else {
                enqueueSnackbar(resp?.mensaje || 'Error al cargar lotes', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar('Error al cargar lotes', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filtroEstado, filtroModulo, filtroBusqueda, page, pageSize, enqueueSnackbar]);

    const cargarMetricas = useCallback(async () => {
        try {
            const resp = await cargaMasivaMetricasJWT();
            if (resp?.ok) setMetricas(resp.metricas);
        } catch (err) {
            // silencioso
        }
    }, []);

    useEffect(() => { cargarLotes(); }, [cargarLotes]);
    useEffect(() => { cargarMetricas(); }, [cargarMetricas]);

    useEffect(() => {
        getCombosJWT('reparticiones', '', 500)
            .then(r => {
                if (r?.ok && r.items) {
                    // Deduplicar por label: Organismos_Reparticiones tiene entradas repetidas
                    // con distinto Id. Preferimos el Id más bajo (más viejo) — por convención
                    // los Ids negativos o bajos son los "canónicos" del sistema.
                    const mejorPorLabel = new Map();
                    for (const item of r.items) {
                        const key = (item.label || '').trim().toUpperCase();
                        if (!key) continue;
                        const actual = mejorPorLabel.get(key);
                        if (!actual || Number(item.id) < Number(actual.id)) {
                            mejorPorLabel.set(key, item);
                        }
                    }
                    const dedup = Array.from(mejorPorLabel.values())
                        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                    setReparticiones(dedup);
                }
            })
            .catch(() => {});
    }, []);

    const validarFormCrear = () => {
        if (!crearForm.idModulo || !crearForm.idReparticion) {
            enqueueSnackbar('Seleccioná módulo y repartición', { variant: 'warning' });
            return false;
        }
        if (!crearForm.expAdm?.Id) {
            enqueueSnackbar('Seleccioná un expediente administrativo', { variant: 'warning' });
            return false;
        }
        return true;
    };

    const handleCrearLote = async () => {
        if (!validarFormCrear()) return;

        // Módulo 1 = Tributarias → los lotes nacen desde "Recepción DGR"
        // (auditar → revisar → generar lote sobre los certificados que DGR empuja).
        // Ya no se consulta DGR en vivo: ese WS no expone causas (solo pagos).
        if (parseInt(crearForm.idModulo) === 1) {
            setDialogCrearOpen(false);
            enqueueSnackbar('Los lotes tributarios se generan desde "Recepción DGR".', { variant: 'info' });
            navigate('/carga-masiva/recepcion-dgr');
            return;
        }

        // Módulo 2 = No Tributarias → flujo tradicional (Excel después)
        setCrearLoading(true);
        try {
            const resp = await cargaMasivaCrearLoteJWT({
                idModulo: parseInt(crearForm.idModulo),
                idReparticion: parseInt(crearForm.idReparticion),
                observaciones: crearForm.observaciones || undefined,
                idExpedienteAdministrativo: crearForm.expAdm.Id,
                cantidadEsperada: crearForm.cantidadEsperada ? parseInt(crearForm.cantidadEsperada) : null,
            });
            if (resp?.ok) {
                enqueueSnackbar(resp.mensaje, { variant: 'success' });
                setDialogCrearOpen(false);
                setCrearForm({ idModulo: '', idReparticion: '', observaciones: '', expAdm: null, cantidadEsperada: '' });
                cargarLotes();
                cargarMetricas();
                navigate(`/carga-masiva/${resp.idLote}`);
            } else {
                enqueueSnackbar(resp?.mensaje || 'Error al crear lote', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar('Error al crear lote', { variant: 'error' });
        } finally {
            setCrearLoading(false);
        }
    };

    const handleEliminarConfirm = async () => {
        if (!loteAEliminar) return;
        try {
            const resp = await cargaMasivaEliminarLoteJWT(loteAEliminar.Id);
            if (resp?.ok) {
                enqueueSnackbar('Lote eliminado', { variant: 'success' });
                cargarLotes();
                cargarMetricas();
            } else {
                enqueueSnackbar(resp?.mensaje || 'Error al eliminar', { variant: 'error' });
            }
        } finally {
            setDialogEliminarOpen(false);
            setLoteAEliminar(null);
        }
    };

    const METRICAS_CONFIG = [
        { key: 'borradores', label: 'Borradores', icon: <EditIcon />, color: '#9e9e9e', estado: '0' },
        { key: 'importados', label: 'Importados', icon: <UploadFileIcon />, color: '#0288d1', estado: '1' },
        { key: 'enAuditoria', label: 'En auditoría', icon: <DescriptionIcon />, color: '#f57c00', estado: '2' },
        { key: 'auditados', label: 'Auditados', icon: <CheckIcon />, color: '#7b1fa2', estado: '3' },
        { key: 'abogadosAsignados', label: 'Con abogado', icon: <AssignmentIcon />, color: '#5e35b1', estado: '4' },
        { key: 'pdfsGenerados', label: 'PDFs', icon: <PdfIcon />, color: '#c2185b', estado: '5' },
        { key: 'enviadosPJ', label: 'Enviados PJ', icon: <SendIcon />, color: '#00897b', estado: '6' },
        { key: 'pasadosSIGIF', label: 'Pasados SIGIF', icon: <GavelIcon />, color: '#2e7d32', estado: '7' },
    ];

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
            <Container maxWidth={false} disableGutters>
                {/* Header compacto: título + stats inline + controles */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
                    mb: '12px',
                }}>
                    <Box sx={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UploadFileIcon sx={{ color: COLOR, fontSize: 30 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                            Carga Masiva
                        </Typography>
                    </Box>

                    {/* Stats inline (clickeables como filtro) */}
                    {metricas && (
                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {METRICAS_CONFIG.map(m => {
                                const activo = filtroEstado === m.estado;
                                return (
                                    <Box key={m.key}
                                        onClick={() => { setFiltroEstado(activo ? '' : m.estado); setPage(0); }}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            bgcolor: activo ? m.color : 'white',
                                            color: activo ? 'white' : m.color,
                                            border: `1px solid ${m.color}`,
                                            borderRadius: '4px',
                                            px: '10px', py: '4px', cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            '&:hover': { bgcolor: activo ? m.color : `${m.color}18` },
                                        }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{metricas[m.key] ?? 0}</Typography>
                                        <Typography sx={{ fontSize: 11 }}>{m.label}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {/* Controles */}
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Tooltip title="Gestionar plantillas">
                            <IconButton onClick={() => navigate('/carga-masiva/plantillas')} size="small"
                                sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: `${BORDER_RADIUS}px` }}>
                                <ArticleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Refrescar">
                            <IconButton onClick={() => { cargarLotes(); cargarMetricas(); }} size="small"
                                sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: `${BORDER_RADIUS}px` }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {puedeCrear && (
                            <Button
                                variant="outlined" size="small"
                                startIcon={<FactCheckIcon />}
                                onClick={() => navigate('/carga-masiva/recepcion-dgr')}
                                sx={{
                                    color: COLOR, borderColor: COLOR,
                                    borderRadius: `${BORDER_RADIUS}px`, textTransform: 'none', fontWeight: 600,
                                }}
                            >
                                Recepción DGR
                            </Button>
                        )}
                        {puedeCrear && (
                            <Button
                                variant="contained" size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setDialogCrearOpen(true)}
                                sx={{
                                    bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' },
                                    borderRadius: `${BORDER_RADIUS}px`, textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Nuevo Lote
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Filtros */}
                <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: `${BORDER_RADIUS}px` }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            size="small" select label="Estado" value={filtroEstado}
                            onChange={e => { setFiltroEstado(e.target.value); setPage(0); }}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">Todos los estados</MenuItem>
                            {Object.entries(ESTADOS).map(([v, e]) => (
                                <MenuItem key={v} value={v}>{e.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small" select label="Módulo" value={filtroModulo}
                            onChange={e => { setFiltroModulo(e.target.value); setPage(0); }}
                            sx={{ minWidth: 180 }}
                        >
                            <MenuItem value="">Todos los módulos</MenuItem>
                            <MenuItem value="1">Tributarias</MenuItem>
                            <MenuItem value="2">No Tributarias</MenuItem>
                        </TextField>
                        <TextField
                            size="small" label="Buscar por ID u observaciones"
                            value={filtroBusqueda}
                            onChange={e => setFiltroBusqueda(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { setPage(0); cargarLotes(); }
                                if (e.key === 'Escape') { setFiltroBusqueda(''); setPage(0); }
                            }}
                            placeholder="Enter para buscar, Escape para limpiar"
                            sx={{ flex: 1, minWidth: 280 }}
                        />
                    </Box>
                </Paper>

                {/* Tabla de lotes */}
                <Paper sx={{ borderRadius: `${BORDER_RADIUS}px`, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress sx={{ color: COLOR }} />
                        </Box>
                    ) : lotes.length === 0 ? (
                        <Box sx={{ p: 8, textAlign: 'center', color: 'text.secondary' }}>
                            <SearchOffIcon sx={{ fontSize: 64, color: 'rgba(0,0,0,0.15)', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>No hay lotes para mostrar</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {puedeCrear ? 'Creá uno nuevo para empezar a importar demandas.' : 'Pedile a un administrador que cree uno.'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'rgba(106, 27, 154, 0.05)' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Id</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Fecha</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Módulo</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Repartición</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Exp. Adm</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Estado</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: COLOR }}>Filas</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: COLOR }}>OK</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: COLOR }}>Defect.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: COLOR }}>Monto total</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: COLOR }}>Usuario</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: COLOR }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                            <TableBody>
                                {lotes.map(l => {
                                    const e = ESTADOS[l.Estado] || { label: `Estado ${l.Estado}`, color: 'default' };
                                    return (
                                        <TableRow key={l.Id} hover sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/carga-masiva/${l.Id}`)}>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{l.Id}</TableCell>
                                            <TableCell>{l.Fecha_Creacion ? new Date(l.Fecha_Creacion).toLocaleDateString() : ''}</TableCell>
                                            <TableCell>{l.Id_Modulo === 1 ? 'Tributarias' : l.Id_Modulo === 2 ? 'No Trib.' : '-'}</TableCell>
                                            <TableCell>{l.Reparticion_Nombre || l.Id_Reparticion}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }} onClick={e => e.stopPropagation()}>
                                                {l.Expediente_Adm_Numero ? (
                                                    <Tooltip title={l.Expediente_Adm_Caratula || ''}>
                                                        <Chip size="small" icon={<DescriptionIcon fontSize="small" />}
                                                            label={l.Expediente_Adm_Numero}
                                                            onClick={() => window.open(`/expediente/administrativo/${l.Id_Expediente_Administrativo}`, '_blank')}
                                                            sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.72rem' }} />
                                                    </Tooltip>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell><Chip label={e.label} color={e.color} size="small" /></TableCell>
                                            <TableCell align="right">{l.Total_Filas || 0}</TableCell>
                                            <TableCell align="right">{l.Filas_OK || 0}</TableCell>
                                            <TableCell align="right">{l.Filas_Defectuosas || 0}</TableCell>
                                            <TableCell align="right">{Number(l.Monto_Total_Lote || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell>{l.Usuario_Creador}</TableCell>
                                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                                                <Tooltip title="Ver detalle">
                                                    <IconButton size="small" onClick={() => navigate(`/carga-masiva/${l.Id}`)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {puedeEliminar && (l.Estado === 0 || l.Estado === 1) && (
                                                    <Tooltip title="Eliminar lote">
                                                        <IconButton size="small" onClick={() => { setLoteAEliminar(l); setDialogEliminarOpen(true); }}>
                                                            <DeleteIcon fontSize="small" color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div" count={total} page={page} rowsPerPage={pageSize}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </>
                )}
            </Paper>

            {/* Dialog crear lote */}
            <Dialog open={dialogCrearOpen} onClose={() => setDialogCrearOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: `${BORDER_RADIUS}px` } }}>
                <DialogTitle sx={{ color: COLOR, fontWeight: 700 }}>Nuevo Lote de Carga Masiva</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            select fullWidth label="Módulo"
                            value={crearForm.idModulo}
                            onChange={e => {
                                const nuevoModulo = e.target.value;
                                setCrearForm(f => {
                                    // Si elige Tributarias, pre-seleccionar DIRECCION GENERAL DE RENTAS
                                    if (nuevoModulo === '1' && !f.idReparticion) {
                                        const rentas = reparticiones.find(r =>
                                            /RENTAS|DGR/i.test(r.label || '')
                                        );
                                        return { ...f, idModulo: nuevoModulo, idReparticion: rentas ? rentas.id : '' };
                                    }
                                    return { ...f, idModulo: nuevoModulo };
                                });
                            }}
                        >
                            <MenuItem value="1">Tributarias</MenuItem>
                            <MenuItem value="2">No Tributarias</MenuItem>
                        </TextField>
                        <Autocomplete
                            options={reparticiones}
                            getOptionLabel={(o) => o?.label || ''}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            value={reparticiones.find(r => r.id === crearForm.idReparticion) || null}
                            onChange={(_, v) => setCrearForm(f => ({ ...f, idReparticion: v?.id || '' }))}
                            renderInput={(params) => (
                                <TextField {...params} label="Repartición (organismo que envía)" fullWidth />
                            )}
                        />
                        <ExpedienteSelector
                            value={crearForm.expAdm}
                            onChange={(v) => setCrearForm(f => ({ ...f, expAdm: v }))}
                        />
                        <TextField
                            fullWidth type="number" label="Cantidad esperada (opcional)"
                            placeholder="Ej: 5 (según el expediente físico)"
                            helperText="Cantidad de causas que debería traer el lote según el expediente físico. Si al consultar DGR no coinciden, se avisa."
                            value={crearForm.cantidadEsperada}
                            onChange={e => setCrearForm(f => ({ ...f, cantidadEsperada: e.target.value }))}
                            InputProps={{ inputProps: { min: 1, max: 999 } }}
                        />
                        <TextField
                            fullWidth label="Observaciones (opcional)" multiline rows={3}
                            value={crearForm.observaciones}
                            onChange={e => setCrearForm(f => ({ ...f, observaciones: e.target.value }))}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogCrearOpen(false)} disabled={crearLoading}>Cancelar</Button>
                    <Button onClick={handleCrearLote} variant="contained" disabled={crearLoading}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                        {crearLoading
                            ? <CircularProgress size={20} />
                            : (parseInt(crearForm.idModulo) === 1 ? 'Siguiente: Consultar DGR →' : 'Crear')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog eliminar */}
            <Dialog open={dialogEliminarOpen} onClose={() => setDialogEliminarOpen(false)} PaperProps={{ sx: { borderRadius: `${BORDER_RADIUS}px` } }}>
                <DialogTitle>¿Eliminar lote?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Se eliminará el lote <strong>{loteAEliminar?.Id}</strong> y todas sus filas importadas.
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogEliminarOpen(false)}>Cancelar</Button>
                    <Button onClick={handleEliminarConfirm} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>
            </Container>
        </Box>
    );
}
